import { MATRIX_GROUPS, MATRIX_POINT_COLORS, type MatrixGroup } from '@/api/groups-matrix'
import { Col } from '@/app/components/Flex/Flex'
import { Num } from '@/app/components/Num/Num'
import { Spinner } from '@/app/components/Spinner/Spinner'
import { Text } from '@/app/components/Text/Text'
import { UI } from '@/app/components/UI/UI'
import { useMatrix } from '@/app/state/matrix'
import { getMatrixSectionQuestion } from '@/app/utils/matrix-groups'
import type { FunctionalComponent } from 'preact'
import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper/types'
import { useShallow } from 'zustand/shallow'
import styles from './index.module.scss'

type MatrixSectionsProps = {
  initialGroupId?: string
  onGroupChange?: (groupId: string) => void
  onSectionClick: (group: MatrixGroup, question: string) => void
  onClose: () => void
}

export const MatrixSections: FunctionalComponent<MatrixSectionsProps> = ({
  initialGroupId,
  onGroupChange,
  onSectionClick,
  onClose,
}) => {
  const fetch = useMatrix(s => s.fetch)
  const matrix = useMatrix(useShallow(s => s.matrix))
  const swiperRef = useRef<SwiperType | null>(null)
  const initialIdx = useMemo(
    () => Math.max(0, MATRIX_GROUPS.findIndex(group => group.id === initialGroupId)),
    [initialGroupId],
  )
  const [activeIdx, setActiveIdx] = useState(initialIdx)

  useEffect(() => {
    fetch()
  }, [])

  useEffect(() => {
    setActiveIdx(initialIdx)

    if (swiperRef.current && swiperRef.current.activeIndex !== initialIdx) {
      swiperRef.current.slideTo(initialIdx, 0)
    }
  }, [initialIdx])

  return (
    <>
      <div
        class={styles.overlay}
        onClick={onClose}
      />

      <UI.Panel
        shadow
        radius='m'
        class={styles.root}
      >
        <UI.Tabs>
          {MATRIX_GROUPS.map((group, idx) => (
            <UI.Tab
              key={group.id}
              height={47}
              active={activeIdx === idx}
              onClick={() => {
                setActiveIdx(idx)
                onGroupChange?.(group.id)
                swiperRef.current?.slideTo(idx)
              }}
            >
              {group.title}
            </UI.Tab>
          ))}
        </UI.Tabs>

        <div class={styles.swiperWrapper}>
          <Swiper
            initialSlide={initialIdx}
            slidesPerView={1}
            onSwiper={(swiper) => {
              swiperRef.current = swiper

              if (swiper.activeIndex !== initialIdx) {
                swiper.slideTo(initialIdx, 0)
              }
            }}
            onSlideChange={(swiper) => {
              setActiveIdx(swiper.activeIndex)
              const group = MATRIX_GROUPS[swiper.activeIndex]

              if (group) {
                onGroupChange?.(group.id)
              }
            }}
          >
            {MATRIX_GROUPS.map(group => (
              <SwiperSlide key={group.id} class={styles.swiperSlide}>
                <div class={styles.table}>
                  {group.sections.map(section => (
                    <button
                      disabled={!matrix}
                      key={section.id}
                      type='button'
                      class={styles.row}
                      onClick={() => {
                        onSectionClick(group, getMatrixSectionQuestion(section, matrix))
                      }}
                    >
                      <Text
                        fontSize={16}
                        class={styles.name}
                      >
                        {section.title}
                      </Text>

                      {!matrix ? <Spinner color='pink' size={16} /> : (
                        <Col class={styles.points}>
                          {matrix && section.points.map(point => (
                            <Num
                              key={point}
                              color={MATRIX_POINT_COLORS[point]}
                            >
                              {matrix[point]}
                            </Num>
                          ))}
                        </Col>
                      )}
                    </button>
                  ))}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </UI.Panel>
    </>
  )
}
